<template>
    <v-dialog v-model="dialog" persistent max-width="800px">
      <v-card>
        <v-card-text class="ma-4">
          <h2 class="mt-4 font-italic">Gebruiksvoorwaarden</h2>
          <p>
            Deze overeenkomst (hierna ‘Gebruiksvoorwaarden’) regelt de voorwaarden voor gebruik van de website en daarop beschikbare meetdata en trendgrafieken.
          </p>
          <p>
            Dit is een juridisch bindende overeenkomst tussen u – als individu of handelend namens een rechtspersoon – en Stichting Deltares (hierna “Deltares”). Deze Gebruiksvoorwaarden worden van kracht op het moment dat u uw toestemming geeft door de knop “Ik ga akkoord met de voorwaarden” hieronder aan te vinken.
          </p>
          <p>
            Het gebruik van de website (hierna “Website”) en de (meet)data en trendgrafieken zoals gepresenteerd op de Website (hierna tezamen “Data”) is onderworpen aan deze Gebruiksvoorwaarden zoals hieronder uiteengezet.
          </p>
          <p class="mt-4 font-weight-bold">
            Door het selectievakje “Ik ga akkoord met de voorwaarden” aan te vinken:
          </p>
          <ol>
            <li>
              verklaart u bevoegd te zijn om namens de rechtspersoon die u vertegenwoordigt (hierna “Gebruiker”) deze Gebruiksvoorwaarden te mogen accepteren;
            </li>
            <li>
              gaat Gebruiker akkoord met deze Gebruiksvoorwaarden en verklaart daaraan gebonden te zijn.
            </li>
          </ol>
          <p class="mt-4">
            Als u niet bevoegd bent om namens Gebruiker akkoord te gaan met deze Gebruiksvoorwaarden, vink dan het selectievakje “Ik ga akkoord” NIET aan.
          </p>
          <p>
            U mag de Website en Data niet gebruiken zonder een overeenkomst met Deltares. Indien u niet akkoord gaat met deze Gebruiksvoorwaarden, dient u het gebruik van de Website en Data onmiddellijk te staken. Alle verdere handelingen met betrekking tot de Website en Data zonder een overeenkomst met Deltares, zijn in strijd met het auteursrecht.
          </p>
          <h3 class="mt-4 font-italic">
            Artikel 1. Doel
          </h3>
          <p>
            Het doel van de Website is om, met het oog op de taakuitvoering van waterbeheerders, waterschappen en provincies, op één plek de beschikbare publieke meetdata betreffende de aanwezigheid van vele chemische stoffen in het oppervlaktewater in Nederland te tonen alsmede de ontwikkeling van de aanwezigheid van deze stoffen in de tijd in de vorm van trendgrafieken. De Website geeft geen advies over hoe om te gaan met de Data.
          </p>
          <p>
            De Data die worden getoond zijn afkomstig van de Landelijke Enquête Waterkwaliteit (LEW) <a href="https://www.waterkwaliteitsportaal.nl/rapportage-en-publieksinformatie" target="_blank">https://www.waterkwaliteitsportaal.nl/rapportage-en-publieksinformatie</a> en van Rijkswaterstaat <a href="https://waterinfo.rws.nl" target="_blank">waterinfo.rws.nl</a>
          </p>
          <h3 class="mt-4 font-italic">
            Artikel 2. Gebruik van de Data
          </h3>
          <p>
            De Gebruiker van deze Website stemt er uitdrukkelijk mee in:
          </p>
          <ol type="a">
            <li>
              dat het gebruik van de Website en Data voor zijn/haar eigen risico is;
            </li>
            <li>
              de Website en Data met zorg te interpreteren alvorens deze te gebruiken, teneinde onjuist conclusies te vermijden;
            </li>
            <li>
              dat Gebruiker als enige verantwoordelijk is voor de interpretatie van de Website en Data en de beslissingen die Gebruiker op basis daarvan neemt;
            </li>
            <li>
              dat Gebruiker verantwoordelijk is en blijft voor het uitvoeren van de juiste risicobeoordelingen voorafgaand aan elke activiteit die wordt uitgevoerd op basis van Gebruikers interpretatie van de Website en Data;
            </li>
            <li>
              dat de Website en Data alleen mag worden gebruikt voor wettelijke toegestane doeleinden, op een manier de op geen enkele wijze i) Deltares en de Data-aanleverende instanties kan schaden noch (ii) inbreuk kan maken op de rechten van derden;
            </li>
          </ol>
          <h3 class="mt-4 font-italic">
            Artikel 3. Intellectuele eigendomsrechten
          </h3>
          <p>
            3.1 Het auteursrecht en andere intellectuele eigendomsrechten op de Website en Data (waaronder alle teksten, grafisch materiaal, logo’s en vormgeving) berusten bij en worden voorbehouden aan Deltares respectievelijk de Data-aanleverende instanties, tenzij anders vermeld.
          </p>
          <p>
            3.2 Hergebruik en delen van (delen van) de Data is toegestaan mits met vermelding van de navolgende tekst en voldaan wordt aan de daarin genoemde voorwaarden en de voorwaarden onder b.
          </p>
          <ol class="mt-4" type="a">
            <li>
              “Deze data, hierna “Data”, zijn verkregen vanuit de website <a href="https://chemtrend.netlify.app/trends" target="_blank">https://chemtrend.netlify.app/trends</a> welke tot doel heeft om met het oog op de taakuitvoering van waterbeheerders, waterschappen en provincies, op één plek de beschikbare publieke meetdata betreffende de aanwezigheid van vele chemische stoffen in het oppervlaktewater in Nederland te tonen alsmede de ontwikkeling van de aanwezigheid van deze stoffen in de tijd in de vorm van trendgrafieken. Het gebruik van de Data is onderworpen aan de voorwaarden die u kunt vinden op deze website: <a href="/terms-of-use" target="_blank">https://chemtrend.netlify.app/terms-of-use</a>. Door gebruik van deze Data verklaart u kennis te hebben genomen van deze voorwaarden en daarmee akkoord te gaan”.
            </li>
            <li>
              b. Indien de Data afkomstig is van Informatiehuis Water – Waterkwaliteitsportaal, dan dient Gebruiker te voldoen aan de voorwaarden opgenomen op <a href="https://www.waterkwaliteitsportaal.nl/disclaimer-en-bronvermelding" target="_blank">https://www.waterkwaliteitsportaal.nl/disclaimer-en-bronvermelding</a>. Per januari 2025 kan de tekst die u vermeld in uw publicatie zijn: ‘Deze publicatie gebruikt waterdata beschikbaar gesteld door het Informatiehuis Water. De eigendomsrechten van de gebruikte data en de verantwoordelijkheid voor de kwaliteit daarvan ligt bij de waterbeheerders.’. Raadpleeg altijd de link voor de laatste versie van de disclaimer en bronvermelding tekst. Indien de Data afkomstig is van Rijkswaterstaat, dan dient Gebruiker te voldoen aan de voorwaarden opgenomen op <a href="https://waterinfo.rws.nl/" target="_blank">https://waterinfo.rws.nl/</a>. Per januari 2025 kan de tekst die u vermeld in uw publicatie zijn: ‘Bron en intellectuele eigendomsrechten: Rijkswaterstaat. Op de data is de Creative Common zero verklaring (CC0) van toepassing.’
            </li>
          </ol>
            <h3 class="mt-4 font-italic">
              Artikel 4. Geen garantie
            </h3>
            <p>
              4.1 Hoewel de Website en de Data met de groots mogelijke zorgvuldigheid zijn opgezet danwel bijeengebracht, worden deze “as-is” geleverd zonder enige vorm van garantie.
            </p>
            <p>
              4.2 Deltares wijst uitdrukkelijk elke verwachting van geschiktheid voor een bepaald doel van de hand alsmede elke aansprakelijkheid voor verlies of schade van welke aard dan ook als gevolg van het gebruik van of het vertrouwen op deze Website en/of Data door Gebruiker.
            </p>
            <p>
              4.3 Naar beste weten en overtuiging van Deltares maken de Website en Data geen inbreuk op het auteursrecht of op enig ander intellectueel eigendom van derden. Elke verantwoordelijkheid of aansprakelijkheid van Deltares voor claims, kosten, uitgaven en/of gevolgschade als gevolg van of voortvloeiend uit enige inbreuk op intellectuele eigendomsrechten is uitgesloten.
            </p>
            <h3 class="mt-4 font-italic">
              Artikel 5. Duur
            </h3>
            <p>
              5.1 Deze Gebruiksvoorwaarden worden aangegaan voor de duur van het gebruik van de Website en/of Data door Gebruiker, met uitzondering van artikelen 3, 4, 6 en 7 die van kracht blijven, na het gebruik van de Data door Gebruiker.
            </p>
            <h3 class="mt-4 font-italic">
              Artikel 6. Aansprakelijkheid
            </h3>
            <p>
              6.1 Gebruiker stemt ermee in dat Deltares (inclusief personeel en niet-werknemers van Deltares die werkzaamheden voor Deltares (hebben) verricht) niet aansprakelijk is jegens Gebruiker voor enige schade voortvloeiend uit de Gebruiksvoorwaarden en/of het gebruik van de Website en/of Data, met uit uitzondering van schade veroorzaakt door opzet of bewuste roekeloosheid van Deltares.
            </p>
            <h3 class="mt-4 font-italic">
              Artikel 7. Overige bepalingen
            </h3>
            <p>
              7.1 Het is Gebruiker niet toegestaan rechten en/of verplichtingen voortvloeiende uit deze Gebruiksvoorwaarden geheel of gedeeltelijk aan derden over te dragen zonder voorafgaande schriftelijke toestemming van Deltares.
            </p>
            <p>
              7.2 Alle geschillen die voortvloeien uit en/of verband houden met deze Gebruiksvoorwaarden, en alle overeenkomsten die daaruit voortvloeiend, zullen uitsluitend worden voorgelegd aan de bevoegde rechter te Den Haag, Nederland.
            </p>
            <p>
              7.3 Op deze Gebruiksvoorwaarden en alle daaruit voortvloeiende overeenkomsten is uitsluitend Nederlands recht van toepassing.
            </p>
            <h3 class="mt-4 font-italic">
              Over Deltares
            </h3>
            <p>
              Deze Website is samengesteld door Stichting Deltares, statutair gevestigd te Delft 2629 HV en kantoorhoudende aan de Boussinesqweg 1 te Delft. Deltares is ingeschreven bij de Kamer van Koophandel en Fabrieken onder nummer 41146461. Andere contactgegevens zijn: telefoon: + 31 (0)88 335 8273; e-mail: <a href="mailto:info@deltares.nl">info@deltares.nl</a>.
            </p>
            <p>
              Voor inhoudelijke vragen over de getoonde informatie kunt u contact opnemen via <a href="mailto:chemtrend@deltares.nl">chemtrend@deltares.nl</a>.
            </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="acknowledgeDisclaimer">Ik ga akkoord met de voorwaarden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'Disclaimer',
  data () {
    return {
      dialog: !this.disclaimerAcknowledged
    }
  },
  computed: {
    ...mapState(['disclaimerAcknowledged'])
  },
  watch: {
    disclaimerAcknowledged (value) {
      if (value) {
        this.dialog = false
      }
    }
  },
  methods: {
    ...mapActions(['saveDisclaimerAcknowledgment', 'loadDisclaimerAcknowledgment']),
    acknowledgeDisclaimer () {
      this.saveDisclaimerAcknowledgment(true)
      this.$router.push('/trends')
    }
  }
}
</script>
